import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Upload, Download, Users, Trophy, Globe, CheckCircle } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { CountrySelect } from "@/components/CountrySelect";
import PhotoCropDialog from "@/components/PhotoCropDialog";
import PhotoPickerButtons from "@/components/PhotoPickerButtons";
import { compressImage } from "@/utils/compressImage";

import { API, BACKEND_URL } from '../../utils/apiBase';
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('https://res.cloudinary.com/') || url.startsWith('http://res.cloudinary.com/')) return url;
  if (url.startsWith('/api/uploads/')) return `${BACKEND_URL}${url}`;
  return `${BACKEND_URL}${url}`;
};

export const AthletesTab = ({ athletes, loadData, openEditAthlete, uploadImage }) => {
  const { t } = useI18n();
  const [newAthlete, setNewAthlete] = useState({ name: "", photo_url: "", country: "", world_ranking: null, fid: "", gender: "Male", nextgen: false });
  const [athleteImageFile, setAthleteImageFile] = useState(null);
  const [athleteImagePreview, setAthleteImagePreview] = useState(null);
  const [pendingCropFile, setPendingCropFile] = useState(null);
  const [selectedAthletes, setSelectedAthletes] = useState(new Set());
  const [athleteSortBy, setAthleteSortBy] = useState("name");
  const [athleteFilterGender, setAthleteFilterGender] = useState("ALL");
  const [athleteSearch, setAthleteSearch] = useState("");

  const handleAthleteImageChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file || file.size <= 0) return;
    // Trust the file — Samsung / Chrome Android regularly return File
    // objects with empty or weird MIME ("application/octet-stream") from
    // the native camera. We decode defensively in compressImage and only
    // raise a user-visible error if decoding actually fails.
    try {
      const compressed = await compressImage(file);
      if (!compressed || compressed.size <= 0) throw new Error("decode-failed");
      if (compressed.size > 10 * 1024 * 1024) {
        toast.error(t('athletes.fileTooLarge'));
        return;
      }
      toast.success(t('athletes.photoReady'));
      setPendingCropFile(compressed);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("photo accept failed", err);
      toast.error(t('athletes.onlyPngJpeg'));
    }
  };

  const handleCropConfirm = (croppedFile) => {
    // Release any previous preview blob URL before swapping — otherwise two
    // successive crops leak ~500KB-2MB each on iOS Safari until the tab OOMs.
    setAthleteImagePreview((old) => {
      if (old && old.startsWith("blob:")) {
        try { URL.revokeObjectURL(old); } catch { /* no-op */ }
      }
      return URL.createObjectURL(croppedFile);
    });
    setAthleteImageFile(croppedFile);
    setPendingCropFile(null);
  };

  const handleDeletePhoto = () => {
    setAthleteImagePreview((old) => {
      if (old && typeof old === 'string' && old.startsWith('blob:')) {
        try { URL.revokeObjectURL(old); } catch { /* no-op */ }
      }
      return null;
    });
    setAthleteImageFile(null);
    setNewAthlete((p) => ({ ...p, photo_url: '' }));
  };

  const addAthlete = async () => {
    if (!newAthlete.name || !newAthlete.country) { toast.error(t('athletes.fillRequired')); return; }
    try {
      let photo_url = "";
      if (athleteImageFile) { photo_url = await uploadImage(athleteImageFile); if (!photo_url) return; }
      await axios.post(`${API}/athletes`, { ...newAthlete, photo_url });
      toast.success(t('athletes.added'));
      setNewAthlete({ name: "", photo_url: "", country: "", world_ranking: null, fid: "", gender: "Male", nextgen: false });
      setAthleteImageFile(null); setAthleteImagePreview(null);
      loadData();
    } catch { toast.error(t('athletes.errorAdding')); }
  };

  const deleteAthlete = async (id) => {
    try { await axios.delete(`${API}/athletes/${id}`); toast.success(t('athletes.deleted')); loadData(); }
    catch { toast.error(t('athletes.errorDeleting')); }
  };

  const deleteSelectedAthletes = async () => {
    if (selectedAthletes.size === 0) return;
    if (!window.confirm(t('athletes.confirmDeleteBulk', { count: selectedAthletes.size }))) return;
    try {
      await axios.post(`${API}/athletes/delete-bulk`, { ids: Array.from(selectedAthletes) });
      toast.success(t('athletes.bulkDeleted', { count: selectedAthletes.size }));
      setSelectedAthletes(new Set()); loadData();
    } catch { toast.error(t('common.error')); }
  };

  const toggleAthleteSelection = (id) => {
    setSelectedAthletes(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  const filteredAthletes = (athletes || []).filter(a => a?.id).filter(a => {
    if (athleteFilterGender === "ALL") return true;
    return (a.gender || "Male") === athleteFilterGender;
  }).filter(a => {
    const q = athleteSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      (a.name || '').toLowerCase().includes(q) ||
      (a.country || '').toLowerCase().includes(q) ||
      (a.fid || '').toLowerCase().includes(q)
    );
  }).sort((a, b) => {
    if (athleteSortBy === "name") return a.name.localeCompare(b.name);
    if (athleteSortBy === "ranking") { if (!a.world_ranking) return 1; if (!b.world_ranking) return -1; return a.world_ranking - b.world_ranking; }
    if (athleteSortBy === "country") return a.country.localeCompare(b.country);
    if (athleteSortBy === "gender") return (a.gender || "Male").localeCompare(b.gender || "Male");
    return 0;
  });

  return (
    <>
      <Card className="p-6 mb-6 glass">
        <h2 className="text-5xl font-bold mb-6">{t('athletes.addTitle')}</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Input placeholder={t('athletes.name')} value={newAthlete.name} onChange={e => setNewAthlete({...newAthlete, name: e.target.value})} data-testid="athlete-name-input" className="text-2xl h-16" />
            <CountrySelect value={newAthlete.country} onChange={country => setNewAthlete({...newAthlete, country})} placeholder={t('athletes.country')} />
            <Input type="number" placeholder={t('athletes.worldRanking')} value={newAthlete.world_ranking || ""} onChange={e => setNewAthlete({...newAthlete, world_ranking: e.target.value ? parseInt(e.target.value) : null})} data-testid="athlete-wr-input" className="text-2xl h-16" />
            <Input placeholder={t('athletes.fidPlaceholder')} value={newAthlete.fid || ""} onChange={e => setNewAthlete({...newAthlete, fid: e.target.value})} data-testid="athlete-fid-input" className="text-2xl h-16" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-4 h-16">
              <label className="text-lg text-foreground font-medium">{t('athletes.gender')}:</label>
              <button onClick={() => setNewAthlete({...newAthlete, gender: "Male"})} className={`px-4 py-2 rounded text-lg font-bold transition-all ${newAthlete.gender === "Male" ? 'bg-blue-600 text-white' : 'bg-muted text-foreground'}`} data-testid="athlete-gender-male">{t('athletes.male')}</button>
              <button onClick={() => setNewAthlete({...newAthlete, gender: "Female"})} className={`px-4 py-2 rounded text-lg font-bold transition-all ${newAthlete.gender === "Female" ? 'bg-pink-600 text-white' : 'bg-muted text-foreground'}`} data-testid="athlete-gender-female">{t('athletes.female')}</button>
            </div>
            <div className="flex items-center gap-3 h-16">
              <label className="text-lg text-foreground font-medium">{t('athletes.nextgen')}:</label>
              <button onClick={() => setNewAthlete({...newAthlete, nextgen: !newAthlete.nextgen})} className={`px-4 py-2 rounded text-lg font-bold transition-all ${newAthlete.nextgen ? 'bg-amber-600 text-white' : 'bg-muted text-foreground'}`} data-testid="athlete-nextgen-toggle">{newAthlete.nextgen ? t('athletes.ngOn') : t('athletes.ngOff')}</button>
            </div>
            <div>
              <PhotoPickerButtons
                onChange={handleAthleteImageChange}
                onDelete={(athleteImagePreview || newAthlete.photo_url) ? handleDeletePhoto : undefined}
                testId="athlete-photo"
              />
              <p className="text-base text-muted-foreground italic mt-1">{t('athletes.photo')}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => window.open(`${API}/athletes/template`, '_blank')} variant="outline" size="sm" className="text-sm"><Download size={14} className="mr-1" /> {t('athletes.csvTemplate')}</Button>
              <label>
                <input type="file" accept=".csv,.xlsx,.xls" onChange={async (e) => {
                  const file = e.target.files[0]; if (!file) return;
                  const formData = new FormData(); formData.append('file', file);
                  try { const res = await axios.post(`${API}/athletes/import-csv`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }); const { created, updated, error_count, errors } = res.data; if (error_count > 0) { toast.error(`${created} created, ${updated} updated, ${error_count} errors`); errors.forEach(err => toast.error(`${err.row}: ${err.error}`)); } else { toast.success(`${created} created, ${updated} updated`); } loadData(); } catch { toast.error(t('common.error')); }
                  e.target.value = '';
                }} className="hidden" />
                <Button asChild variant="outline" size="sm" className="text-sm cursor-pointer"><span><Upload size={14} className="mr-1" /> {t('athletes.importCsv')}</span></Button>
              </label>
            </div>
          </div>
          {athleteImagePreview && (<div className="flex items-center gap-3"><img src={athleteImagePreview} alt="Preview" className="w-16 h-16 rounded-full object-cover" /><span className="text-base text-muted-foreground">{t('common.imageReady')}</span></div>)}
          <Button onClick={addAthlete} data-testid="add-athlete-btn" className="text-2xl h-16"><Plus size={28} className="mr-2" /> {t('common.add')}</Button>
        </div>
      </Card>
      <PhotoCropDialog
        file={pendingCropFile}
        onCancel={() => setPendingCropFile(null)}
        onConfirm={handleCropConfirm}
      />

      <div className="mb-4">
        <div className="relative">
          <Input
            type="text"
            placeholder={t('athletes.searchPlaceholder')}
            value={athleteSearch}
            onChange={(e) => setAthleteSearch(e.target.value)}
            data-testid="athlete-search-input"
            className="text-lg h-12 pl-10"
          />
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {athleteSearch && (
            <button
              onClick={() => setAthleteSearch("")}
              data-testid="athlete-search-clear"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-sm"
              type="button"
            >
              ✕
            </button>
          )}
        </div>
        {athleteSearch && (
          <p className="mt-2 text-sm text-muted-foreground" data-testid="athlete-search-count">
            {filteredAthletes.length} {t('common.results')}
          </p>
        )}
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {["name","ranking","country","gender"].map(s => (
          <Button key={s} variant={athleteSortBy === s ? "default" : "outline"} onClick={() => setAthleteSortBy(s)} className="text-base sm:text-lg flex-1 sm:flex-none">
            {s === "name" && <Users size={16} className="sm:mr-2" />}{s === "ranking" && <Trophy size={16} className="sm:mr-2" />}{s === "country" && <Globe size={16} className="sm:mr-2" />}{s === "gender" && <span className="sm:mr-2">&#9893;</span>}
            <span className="hidden sm:inline">{t(`athletes.sortBy${s.charAt(0).toUpperCase()+s.slice(1)}`)}</span>
          </Button>
        ))}
        <span className="hidden sm:block w-px h-8 bg-gray-600 mx-1" />
        {["ALL","Male","Female"].map(g => (
          <Button key={g} variant={athleteFilterGender === g ? "default" : "outline"} onClick={() => setAthleteFilterGender(g)} size="sm"
            className={athleteFilterGender === g && g === "Male" ? "bg-blue-600" : athleteFilterGender === g && g === "Female" ? "bg-pink-600" : ""}>
            {g === "ALL" ? t('common.all') : g === "Male" ? `\u2642 ${t('athletes.male')}` : `\u2640 ${t('athletes.female')}`}
          </Button>
        ))}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => {
          if (selectedAthletes.size === filteredAthletes.length) setSelectedAthletes(new Set());
          else setSelectedAthletes(new Set(filteredAthletes.map(a => a.id)));
        }} data-testid="select-all-athletes-btn"><CheckCircle size={16} className="mr-2" />{selectedAthletes.size > 0 ? t('athletes.deselectAll') : t('athletes.selectAll')}</Button>
        {selectedAthletes.size > 0 && (<Button variant="destructive" size="sm" onClick={deleteSelectedAthletes} data-testid="delete-selected-athletes-btn"><Trash2 size={16} className="mr-2" />{t('athletes.deleteSelected', { count: selectedAthletes.size })}</Button>)}
        {selectedAthletes.size > 0 && (<span className="text-sm text-muted-foreground">{selectedAthletes.size} {t('athletes.selected')}</span>)}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredAthletes.map(athlete => (
          <Card key={athlete.id} className={`p-5 glass transition-all ${selectedAthletes.has(athlete.id) ? 'ring-2 ring-primary bg-primary/10' : ''}`} data-testid={`athlete-card-${athlete.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <input type="checkbox" checked={selectedAthletes.has(athlete.id)} onChange={() => toggleAthleteSelection(athlete.id)} className="w-5 h-5 rounded border-gray-500 accent-purple-500 cursor-pointer flex-shrink-0" data-testid={`select-athlete-${athlete.id}`} />
                {athlete.photo_url && <img src={getImageUrl(athlete.photo_url)} alt={athlete.name} className="w-24 h-24 rounded-full object-cover" />}
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-3xl">{athlete.name}</h3>
                    <span style={{ fontSize: '1.2rem', color: athlete.gender === 'Female' ? '#ec4899' : '#3b82f6' }}>{athlete.gender === 'Female' ? '\u2640' : '\u2642'}</span>
                    {athlete.nextgen && <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/40" title="NextGen">NG</span>}
                  </div>
                  <p className="text-xl text-muted-foreground">{athlete.country}</p>
                  <div className="flex gap-3 items-center">
                    <p className="text-2xl font-bold text-primary">#{athlete.world_ranking || 'N/A'}</p>
                    {athlete.fid && <p className="text-lg text-muted-foreground">{athlete.fid}</p>}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => openEditAthlete(athlete)} data-testid={`edit-athlete-${athlete.id}`} className="hover:bg-gray-700"><Edit size={18} className="text-foreground" /></Button>
                <Button variant="destructive" size="sm" onClick={() => deleteAthlete(athlete.id)} data-testid={`delete-athlete-${athlete.id}`}><Trash2 size={18} /></Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </>
  );
};
